/**
 * OpenAI Pixel — client-side helper.
 *
 * The base pixel + init fire from src/app/layout.tsx in <head>, before any
 * React component mounts. This module is the thin wrapper client components
 * import to report conversions afterwards.
 *
 * Design choices (deliberately mirroring src/lib/metaPixel.ts so the two
 * pixels behave identically at every call site):
 *  - SSR-safe: every call no-ops on the server (typeof window === 'undefined')
 *    so importing this from a 'use client' component that Next.js pre-renders
 *    doesn't crash the build.
 *  - Fail-open: if the pixel is disabled (blank NEXT_PUBLIC_OPENAI_PIXEL_ID)
 *    or the SDK hasn't loaded yet, this is a silent no-op. Analytics must
 *    never break a customer mid-booking.
 *  - Queue-tolerant: the snippet installs a stub that queues calls until the
 *    real SDK arrives, so an early call is buffered rather than lost.
 */

declare global {
  interface Window {
    oaiq?: (command: string, eventName: string, params?: Record<string, unknown>) => void;
  }
}

/** True only when the pixel stub or SDK is present on this page. */
function ready(): boolean {
  return typeof window !== 'undefined' && typeof window.oaiq === 'function';
}

/**
 * Report a conversion to the OpenAI Pixel.
 *
 * @param eventName OpenAI's event name, e.g. 'appointment_scheduled'.
 * @param params    Event attributes, e.g. { type: 'customer_action' }.
 */
export function measureOpenAiEvent(
  eventName: string,
  params?: Record<string, unknown>
): void {
  if (!ready()) return;
  try {
    window.oaiq!('measure', eventName, params || {});
  } catch (err) {
    // Swallow — an analytics failure must never interrupt a booking.
    console.warn('[openaiPixel] measure failed:', err);
  }
}
